import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import CareerMatrix from './components/Matrix/CareerMatrix'
import Header from './components/ui/Header'
import PositionModal from './components/Modal/PositionModal'
import { BackgroundPaths } from './components/ui/BackgroundPaths'
import { enrichPositionWithCareerPaths } from './utils/dataHelpers'

// Админские компоненты
import LoginPage from './admin/pages/LoginPage'
import AdminLayout from './admin/components/AdminLayout'
import ProtectedRoute from './admin/components/ProtectedRoute'
import DashboardPage from './admin/pages/DashboardPage'
import DepartmentsPage from './admin/pages/DepartmentsPage'
import TransitionsPage from './admin/pages/TransitionsPage'
import AuditPage from './admin/pages/AuditPage'
import SettingsPage from './admin/pages/SettingsPage'
import { isAuthenticated } from './admin/utils/auth'

// Публичная часть приложения  
const PublicApp = () => {
  const [selectedPosition, setSelectedPosition] = useState(null)
  const [highlightedPositions, setHighlightedPositions] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showMatrix, setShowMatrix] = useState(false)

  // Обработчик клика по должности
  const handlePositionClick = async (position) => {
    try {
      // Обогащаем позицию данными о карьерных путях
      const enrichedPosition = await enrichPositionWithCareerPaths(position)
      setSelectedPosition(enrichedPosition)
      
      // Получаем ID возможных переходов для подсветки
      const careerPathIds = enrichedPosition.careerPaths?.map(cp => cp.id) || []
      setHighlightedPositions(careerPathIds)
      
      setIsModalOpen(true)
    } catch (error) {
      console.error('Ошибка обработки клика по должности:', error)
      setSelectedPosition(position)
      setHighlightedPositions([])
      setIsModalOpen(true)
    }
  }

  // Закрытие модального окна
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPosition(null)
    setHighlightedPositions([])
  }

  // Переключение на матрицу
  const handleShowMatrix = () => {
    setShowMatrix(true)
  }

  // Если показываем матрицу
  if (showMatrix) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Заголовок */}
        <Header />
        
        {/* Основной контент */}
        <main className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Описание */}
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-xl text-gray-600 mb-4">
                Интерактивная матрица карьерных возможностей
              </h2>
            </div>

            {/* Матрица должностей */}
            <CareerMatrix
              selectedPosition={selectedPosition}
              highlightedPositions={highlightedPositions}
              onPositionClick={handlePositionClick}
            />
          </motion.div>
        </main>

        {/* Модальное окно с деталями должности */}
        <PositionModal
          position={selectedPosition}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </div>
    )
  }

  // Показываем красивую заставку
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Заголовок */}
      <Header />
      
      {/* Красивая заставка */}
      <BackgroundPaths onButtonClick={handleShowMatrix} />
    </div>
  )
}

// Функция определения базового пути - всегда корневой для нового домена
const getBasePath = () => {
  return '/'
}

// Главное приложение с роутингом
function App() {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(null) // null = не проверяли, true/false = результат
  const basePath = getBasePath()

  // Проверка аутентификации только для админских маршрутов
  // Избегаем лишних запросов на публичных страницах
  useEffect(() => {
    const checkAuth = async () => {
      // Проверяем только если пользователь на админском маршруте
      if (window.location.pathname.startsWith('/admin')) {
        const authenticated = await isAuthenticated()
        setIsAdminAuthenticated(authenticated)
      }
      // На публичных маршрутах не трогаем состояние, чтобы не сбрасывать валидную сессию
      // Если пользователь вернется в админку, ProtectedRoute сам проверит сессию если isAdminAuthenticated === null
    }
    checkAuth()
  }, [])

  const handleAdminLogin = () => {
    setIsAdminAuthenticated(true)
  }

  return (
    <Router basename={basePath}>
      <Routes>
        {/* Публичные маршруты */}
        <Route path="/" element={<PublicApp />} />
        
        {/* Админские маршруты */}
        <Route 
          path="/admin/login" 
          element={
            isAdminAuthenticated ? 
              <Navigate to="/admin/dashboard" replace /> : 
              <LoginPage onLogin={handleAdminLogin} />
          } 
        />
        
        <Route 
          path="/admin/*" 
          element={
            <ProtectedRoute 
              isAuthenticated={isAdminAuthenticated} 
              setIsAuthenticated={setIsAdminAuthenticated}
            >
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          {/* Вложенные админские маршруты */}
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="departments" element={<DepartmentsPage />} />
          <Route path="transitions" element={<TransitionsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="" element={<Navigate to="dashboard" replace />} />
        </Route>

        {/* Редирект неизвестных маршрутов */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  )
}

export default App
